// Original items written for this site, based on the widely published descriptions of the
// nine Enneagram types. Not the RHETI, the Riso-Hudson test or any other licensed
// instrument: an independent, informal self-reflection quiz.

export type EnneagramType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export const ENNEAGRAM_TYPES: EnneagramType[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export interface EnneagramQuestion {
  id: string;
  text: string;
  type: EnneagramType;
}

export const ENNEAGRAM_QUESTIONS: EnneagramQuestion[] = [
  { id: 'n1a', text: 'I notice mistakes quickly and feel a pull to correct them.', type: 1 },
  { id: 'n2a', text: 'I often put other people’s needs ahead of my own.', type: 2 },
  {
    id: 'n3a',
    text: 'Reaching my goals and being seen as successful matters a lot to me.',
    type: 3,
  },
  {
    id: 'n4a',
    text: 'I often feel different from other people, as if something is missing.',
    type: 4,
  },
  { id: 'n5a', text: 'I need plenty of time alone to recharge and think things through.', type: 5 },
  { id: 'n6a', text: 'I tend to imagine what could go wrong and prepare for it.', type: 6 },
  {
    id: 'n7a',
    text: 'I keep my options open because I hate missing out on something fun.',
    type: 7,
  },
  { id: 'n8a', text: 'I say what I think directly, even if it causes conflict.', type: 8 },
  { id: 'n9a', text: 'I go along with others to keep the peace.', type: 9 },

  {
    id: 'n1b',
    text: 'I hold myself to high standards and feel frustrated when I fall short.',
    type: 1,
  },
  { id: 'n2b', text: 'I feel good about myself when someone needs my help.', type: 2 },
  { id: 'n3b', text: 'I adapt how I present myself depending on who I am with.', type: 3 },
  { id: 'n4b', text: 'My feelings are intense, and I want them to be understood.', type: 4 },
  {
    id: 'n5b',
    text: 'I would rather observe and understand a situation before joining in.',
    type: 5,
  },
  { id: 'n6b', text: 'Loyalty and trust are the qualities I value most in people.', type: 6 },
  {
    id: 'n7b',
    text: 'When things get heavy, I shift my attention to something more positive.',
    type: 7,
  },
  { id: 'n8b', text: 'I take charge naturally and dislike being controlled.', type: 8 },
  {
    id: 'n9b',
    text: 'I can see every side of an argument, which makes it hard to pick one.',
    type: 9,
  },

  {
    id: 'n1c',
    text: 'There is usually a right way to do things, and I try to follow it.',
    type: 1,
  },
  { id: 'n2c', text: 'I find it hard to say no when someone asks me for something.', type: 2 },
  { id: 'n3c', text: 'I am productive and efficient, and I hate wasting time.', type: 3 },
  { id: 'n4c', text: 'I am drawn to beauty, art and things with deep meaning.', type: 4 },
  { id: 'n5c', text: 'I guard my time and energy because they feel limited.', type: 5 },
  { id: 'n6c', text: 'I question authority but also want someone I can rely on.', type: 6 },
  { id: 'n7c', text: 'I get bored easily and love planning the next adventure.', type: 7 },
  { id: 'n8c', text: 'I protect the people I care about fiercely.', type: 8 },
  { id: 'n9c', text: 'I put off things I don’t want to deal with.', type: 9 },

  {
    id: 'n1d',
    text: 'I feel responsible for making things better, around me and in the world.',
    type: 1,
  },
  { id: 'n2d', text: 'I can sense what others are feeling and what would help them.', type: 2 },
  { id: 'n3d', text: 'I find it hard to slow down or stop working toward the next goal.', type: 3 },
  {
    id: 'n4d',
    text: 'Being authentic, true to who I really am, matters more to me than fitting in.',
    type: 4,
  },
  { id: 'n5d', text: 'I like to become an expert in the subjects that interest me.', type: 5 },
  { id: 'n6d', text: 'I often seek reassurance before making an important decision.', type: 6 },
  {
    id: 'n7d',
    text: 'I am optimistic and full of ideas, often more than I can follow through on.',
    type: 7,
  },
  { id: 'n8d', text: 'Showing weakness or vulnerability does not come easily to me.', type: 8 },
  {
    id: 'n9d',
    text: 'Feeling calm and comfortable is more important to me than getting my way.',
    type: 9,
  },
];

export const ENNEAGRAM_BLURBS: Record<EnneagramType, { name: string; blurb: string }> = {
  1: {
    name: 'The Reformer',
    blurb:
      'Principled, conscientious and driven to improve things. At best wise and fair; under stress self-critical and rigid. Growth comes from accepting that good enough often is.',
  },
  2: {
    name: 'The Helper',
    blurb:
      'Warm, generous and attuned to others. At best caring without strings; under stress people-pleasing and resentful when unappreciated. Growth comes from naming your own needs.',
  },
  3: {
    name: 'The Achiever',
    blurb:
      'Ambitious, adaptable and focused on results. At best inspiring and genuine; under stress image-conscious and overworked. Growth comes from valuing who you are, not only what you do.',
  },
  4: {
    name: 'The Individualist',
    blurb:
      'Expressive, sensitive and searching for meaning and identity. At best creative and emotionally honest; under stress moody and withdrawn. Growth comes from noticing what is already present.',
  },
  5: {
    name: 'The Investigator',
    blurb:
      'Curious, perceptive and independent. At best insightful and innovative; under stress detached and hoarding energy. Growth comes from engaging before you feel fully ready.',
  },
  6: {
    name: 'The Loyalist',
    blurb:
      'Committed, responsible and alert to risk. At best courageous and trustworthy; under stress anxious and suspicious. Growth comes from trusting your own inner guidance.',
  },
  7: {
    name: 'The Enthusiast',
    blurb:
      'Spontaneous, versatile and optimistic. At best joyful and grateful; under stress scattered and avoiding pain. Growth comes from staying with one thing, including difficult feelings.',
  },
  8: {
    name: 'The Challenger',
    blurb:
      'Strong, decisive and protective. At best magnanimous leaders; under stress domineering and confrontational. Growth comes from letting others see your softer side.',
  },
  9: {
    name: 'The Peacemaker',
    blurb:
      'Easygoing, accepting and steady. At best a calm, uniting presence; under stress complacent and stubborn. Growth comes from knowing and voicing what you want.',
  },
};
